import { createContext, useContext, useEffect, useRef, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const NotifCtx = createContext(null);
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Try to request browser desktop notification permission (best-effort, no throw).
function ensureBrowserPermission() {
  if (typeof window === "undefined" || !("Notification" in window)) return "unsupported";
  if (Notification.permission === "granted") return "granted";
  if (Notification.permission === "denied") return "denied";
  try { Notification.requestPermission(); } catch (e) { console.warn("[GTF] Notification.requestPermission failed:", e); }
  return Notification.permission;
}

function showDesktopNotification({ title, body }) {
  try {
    if (!("Notification" in window)) return;
    if (Notification.permission !== "granted") return;
    if (!document.hidden && document.hasFocus()) return; // don't spam foreground
    const n = new Notification(title || "GTF", { body: body || "", icon: "/favicon.ico", tag: "gtf-notif" });
    n.onclick = () => { window.focus(); n.close(); };
  } catch (e) { console.warn("[GTF] Desktop notification failed:", e); }
}

export function NotificationsProvider({ children }) {
  const { user } = useAuth();
  const [tick, setTick] = useState(0);
  const [unread, setUnread] = useState(0);
  const [typingFrom, setTypingFrom] = useState({}); // { [from_user_id]: thread_id }
  const wsRef = useRef(null);
  const typingClearTimers = useRef({}); // per-peer auto-clear timers

  useEffect(() => {
    if (!user) return;
    // Ask for desktop notification permission once logged in
    ensureBrowserPermission();
    const token = localStorage.getItem("gtf_token");
    if (!token) return;
    const wsUrl = BACKEND_URL.replace(/^http/, "ws") + `/api/ws?token=${encodeURIComponent(token)}`;
    let closed = false;
    let ws;
    const connect = () => {
      ws = new WebSocket(wsUrl);
      wsRef.current = ws;
      ws.onmessage = (ev) => {
        try {
          const msg = JSON.parse(ev.data);
          if (msg.type === "notification") {
            toast(msg.data.title, { description: msg.data.body });
            setUnread((u) => u + 1);
            setTick((t) => t + 1);
            showDesktopNotification({ title: msg.data.title, body: msg.data.body });
          } else if (msg.type === "message") {
            setTick((t) => t + 1);
            const d = msg.data || {};
            // Notify only when the message is FOR this user (not their own echo)
            if (d.to_user_id === user.id) {
              showDesktopNotification({
                title: `${d.from_user_name || "New message"}`,
                body: d.image_url ? "📷 sent an image" : (d.body || "").slice(0, 80),
              });
            }
          } else if (msg.type === "message_deleted") {
            setTick((t) => t + 1);
          } else if (msg.type === "typing") {
            const { from_user_id, thread_id, is_typing } = msg.data || {};
            if (!from_user_id) return;
            setTypingFrom((prev) => {
              const next = { ...prev };
              if (is_typing) next[from_user_id] = thread_id || true;
              else delete next[from_user_id];
              return next;
            });
            clearTimeout(typingClearTimers.current[from_user_id]);
            if (is_typing) {
              typingClearTimers.current[from_user_id] = setTimeout(() => {
                setTypingFrom((prev) => { const n = { ...prev }; delete n[from_user_id]; return n; });
              }, 4000);
            }
          }
        } catch (e) { console.warn("[GTF] ws parse error", e); }
      };
      ws.onclose = () => {
        if (!closed) setTimeout(connect, 3000);
      };
    };
    connect();
    return () => { closed = true; try { ws && ws.close(); } catch (e) { console.warn("[GTF] ws close", e); } };
  }, [user]);

  const sendTyping = ({ to_user_id, thread_id, is_typing }) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    try {
      ws.send(JSON.stringify({ type: "typing", to_user_id, thread_id, is_typing: !!is_typing }));
    } catch (e) { console.warn("[GTF] Failed to send typing event:", e); }
  };

  return (
    <NotifCtx.Provider value={{ tick, unread, setUnread, typingFrom, sendTyping }}>
      {children}
    </NotifCtx.Provider>
  );
}

export const useNotifications = () => useContext(NotifCtx) || { tick: 0, unread: 0, setUnread: () => {}, typingFrom: {}, sendTyping: () => {} };
